// src/sections/lp-analysis/NewOverviewSection.js
'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { Box, Card, List, CardContent, Stack, Typography } from '@mui/material';
import {
  Description,
  Group,
  Star,
  CompareArrows,
  VolumeUp,
  Security,
  ThumbUp,
  Cases,
  Gavel,
  Psychology,
  WorkspacePremium,
  LocalOffer,
  CallToAction,
  TrendingUp,
  ChevronRight
} from '@mui/icons-material';
import FeedbackButtons from 'components/FeedbackButtons';

const NewOverviewSection = ({ analysisData }) => {
  const currentTaskId = useSelector((state) => state.currentTaskReducer);
  const [hoveredItems, setHoveredItems] = useState({});

  const safeGet = (obj, key) => {
    return obj && obj[key] && obj[key].trim() ? obj[key] : null;
  };

  const hasSectionContent = (section) => {
    if (!section.items || section.items.length === 0) return false;
    return section.items.some((item) => item.value !== null);
  };

  const groupedData = {
    primary: {
      title: safeGet(analysisData, 'Service Name') || 'Unnamed Service',
      subtitle: analysisData?.Industry ? `Industry: ${analysisData.Industry}` : null,
      fullWidth: true,
      items: [
        {
          icon: <Description />,
          label: 'Description',
          value: safeGet(analysisData, 'Description')
        }
      ]
    },
    positioning: {
      title: 'Market Positioning',
      gridCols: 1,
      items: [
        {
          icon: <Psychology />,
          label: 'Brand Positioning',
          value: safeGet(analysisData, 'Brand Positioning')
        },
        {
          icon: <WorkspacePremium />,
          label: 'Main Value Proposition',
          value: safeGet(analysisData, 'Main Value Proposition')
        }
      ]
    },
    features: {
      title: 'Features & Capabilities',
      gridCols: 1,
      items: [
        {
          icon: <Star />,
          label: 'Key Features',
          value: safeGet(analysisData, 'Key Features')
        },
        {
          icon: <Cases />,
          label: 'Use Cases',
          value: safeGet(analysisData, 'Use Cases')
        },
        {
          icon: <Gavel />,
          label: 'Strengths',
          value: safeGet(analysisData, 'Strengths')
        },
        {
          icon: <TrendingUp />,
          label: 'Competitive Advantages',
          value: safeGet(analysisData, 'Competitive advantages')
        }
      ]
    },
    market: {
      title: 'Market & Audience',
      gridCols: 2,
      items: [
        {
          icon: <Group />,
          label: 'Audience',
          value: safeGet(analysisData, 'Audience')
        },
        {
          icon: <CompareArrows />,
          label: 'Differentiation',
          value: safeGet(analysisData, 'Differentiation')
        }
      ]
    },
    branding: {
      title: 'Brand Elements',
      customLayout: true,
      items: [
        {
          icon: <VolumeUp />,
          label: 'Tone of Voice',
          value: safeGet(analysisData, 'Tone of Voice'),
          fullWidth: true
        },
        {
          icon: <Security />,
          label: 'Trust Factors',
          value: safeGet(analysisData, 'Trust Factors')
        },
        {
          icon: <ThumbUp />,
          label: 'Social Proof',
          value: safeGet(analysisData, 'Social Proof')
        }
      ]
    },
    commercial: {
      title: 'Commercial Information',
      gridCols: 2,
      items: [
        {
          icon: <LocalOffer />,
          label: 'Pricing',
          value: safeGet(analysisData, 'Pricing')
        },
        {
          icon: <CallToAction />,
          label: 'CTA',
          value: safeGet(analysisData, 'CTA')
        }
      ]
    }
  };

  if (!analysisData) {
    return null;
  }

  return (
    <Box sx={{ mt: 0, mb: 8 }}>
      {Object.entries(groupedData)
        .filter(([, section]) => hasSectionContent(section))
        .map(([key, section]) => (
          <motion.div key={key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Card
              sx={{
                mb: 4,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(0,118,255,0.05) 0%, rgba(103,58,183,0.05) 50%, rgba(103,58,183,0) 100%)',
                  zIndex: 0
                }}
              />

              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Stack spacing={3}>
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'primary.main'
                      }}
                    >
                      {section.title}
                    </Typography>
                    {section.subtitle && (
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0, fontWeight: 500 }}>
                        {section.subtitle}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3
                    }}
                  >
                    {section.customLayout ? (
                      <>
                        {section.items
                          .filter((item) => item.value !== null)
                          .map((item, index) => {
                            const itemKey = `${key}-custom-${index}`;
                            return (
                              <Box
                                key={itemKey}
                                onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: true }))}
                                onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: false }))}
                              >
                                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                                  <Box
                                    sx={{
                                      p: 3,
                                      height: '100%',
                                      borderRadius: 2,
                                      bgcolor: 'background.paper',
                                      position: 'relative',
                                      isolation: 'isolate',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        padding: '1px',
                                        borderRadius: 'inherit',
                                        background:
                                          'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        WebkitMaskComposite: 'xor',
                                        maskComposite: 'exclude',
                                        zIndex: -1
                                      },
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                      }
                                    }}
                                  >
                                    <Stack spacing={2}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {item.label}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      {item.label === 'Key Features' ? (
                                        <List component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
                                          {(item.value && typeof item.value === 'string' ? item.value.split(',') : ['...']).map(
                                            (feature, i) => (
                                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <ChevronRight sx={{ color: 'primary.main', fontSize: 20 }} />
                                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                                  {feature.trim()}
                                                </Typography>
                                              </Box>
                                            )
                                          )}
                                        </List>
                                      ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>
                                </motion.div>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                  <FeedbackButtons
                                    blockId={`${section.title.toLowerCase().replace(/\s+/g, '_')}_${item.label
                                      .toLowerCase()
                                      .replace(/\s+/g, '_')}`}
                                    blockType={item.label.toLowerCase().replace(/\s+/g, '_')}
                                    blockTitle={item.label}
                                    blockContent={item.value}
                                    taskId={currentTaskId}
                                    serviceName={analysisData['Service Name']}
                                    isVisible={!!hoveredItems[itemKey]}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: section.fullWidth ? '1fr' : `repeat(${section.gridCols || 3}, 1fr)`
                          },
                          gap: 3
                        }}
                      >
                        {section.items
                          .filter((item) => item.value !== null)
                          .map((item, index) => {
                            const itemKey = `${key}-grid-${index}`;
                            return (
                              <Box
                                key={itemKey}
                                onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: true }))}
                                onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: false }))}
                              >
                                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                                  <Box
                                    sx={{
                                      p: 3,
                                      height: '100%',
                                      borderRadius: 2,
                                      bgcolor: 'background.paper',
                                      position: 'relative',
                                      isolation: 'isolate',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        padding: '1px',
                                        borderRadius: 'inherit',
                                        background:
                                          'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        WebkitMaskComposite: 'xor',
                                        maskComposite: 'exclude',
                                        zIndex: -1
                                      },
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                      }
                                    }}
                                  >
                                    <Stack spacing={2}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {item.label}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      {item.label === 'Key Features' ? (
                                        <List component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
                                          {(item.value && typeof item.value === 'string' ? item.value.split(',') : ['...']).map(
                                            (feature, i) => (
                                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <ChevronRight sx={{ color: 'primary.main', fontSize: 20 }} />
                                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                                  {feature.trim()}
                                                </Typography>
                                              </Box>
                                            )
                                          )}
                                        </List>
                                      ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>
                                </motion.div>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                  <FeedbackButtons
                                    blockId={`${section.title.toLowerCase().replace(/\s+/g, '_')}_${item.label
                                      .toLowerCase()
                                      .replace(/\s+/g, '_')}`}
                                    blockType={item.label.toLowerCase().replace(/\s+/g, '_')}
                                    blockTitle={item.label}
                                    blockContent={item.value}
                                    taskId={currentTaskId}
                                    serviceName={analysisData['Service Name']}
                                    isVisible={!!hoveredItems[itemKey]}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        ))}
    </Box>
  );
};

export default NewOverviewSection;
